import { useCallback, useEffect, useRef, useState } from "react";
import { Target, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { todayISO } from "@/components/crm/crmShared";
import { PipelineBar } from "@/components/crm/PipelineBar";
import { FollowUpQueue } from "@/components/crm/FollowUpQueue";
import { ProspectFilters, ProspectTable } from "@/components/crm/ProspectTable";
import { ProspectSheet } from "@/components/crm/ProspectSheet";

export default function Crm() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState(null);
  const [f, setF] = useState({ status: "", state: "", size: "", q: "" });
  const [open, setOpen] = useState(null);

  const reqId = useRef(0);
  const loadSummary = useCallback(() => api.get("/crm/summary", { params: { today: todayISO() } }).then((r) => setSummary(r.data)).catch(() => {}), []);
  const loadRows = useCallback(() => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    const id = ++reqId.current;
    return api.get("/crm/prospects", { params })
      .then((r) => { if (id === reqId.current) setRows(r.data.prospects); })
      .catch(() => { if (id === reqId.current) setRows([]); });
  }, [f]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { const t = setTimeout(loadRows, f.q ? 250 : 0); return () => clearTimeout(t); }, [loadRows, f.q]);
  const refresh = () => { loadSummary(); loadRows(); };

  return (
    <div data-testid="crm-page" className="p-6 lg:p-10 max-w-[1500px] space-y-8">
      <style>{`@keyframes crmRise{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}.crm-rise{animation:crmRise .45s ease-out both}`}</style>
      <div className="crm-rise">
        <p className="text-xs uppercase tracking-[0.25em] text-amber-400/80 font-semibold flex items-center gap-2"><Target className="h-3.5 w-3.5" /> Sales pipeline</p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-white mt-2">Church Prospects</h1>
        <p className="text-gray-400 mt-2 text-sm">Private to you. Prospects here are separate from paying customer accounts.</p>
      </div>

      {!summary ? <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-400" /></div> : (
        <>
          <PipelineBar counts={summary.counts} total={summary.total} active={f.status} onPick={(s) => setF({ ...f, status: s })} />
          <FollowUpQueue items={summary.follow_ups} today={summary.today} onOpen={setOpen} />
        </>
      )}

      <div data-testid="crm-list" className="rounded-2xl border border-gray-800 bg-card overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-800 space-y-3">
          <div className="flex items-center gap-2"><h2 className="font-display font-semibold text-sm text-white">All prospects</h2><span data-testid="crm-list-count" className="text-xs text-gray-500">{rows ? `${rows.length} shown` : ""}</span></div>
          <ProspectFilters f={f} setF={setF} states={summary?.states || []} onAdd={() => setOpen({})} />
        </div>
        {rows === null ? <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-amber-400" /></div> : <ProspectTable rows={rows} onOpen={setOpen} />}
      </div>

      {open && <ProspectSheet key={open.id || "new"} prospect={open} onClose={() => setOpen(null)} onSaved={refresh} onDeleted={refresh} />}
    </div>
  );
}
